import { Modal } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ModalContainer,
  ModalContent,
  ModalCustomHeader,
  ModalTitle,
} from "./ImagePickerModal.styled";
import Tabs2 from "../../../Tabs2/Tabs2";
import CloseIcon from "@mui/icons-material/Close";
import { FiltersTab, ImagesGrid, Paginations, UploadTab } from "./components";
import useFilters from "./useFilters";
import { useTemplateEditorStore } from "../../hook";
import { observer } from "mobx-react";
import { CurrentFilterEnum } from "../../../../../utils/enums";

const tabsCustomLibrary = [
  {
    label: "Upload",
    permission: null,
  },
  {
    label: "Vehicle Stock",
    permission: null,
  },
];

const MediaLibraryTab = {
  Upload: 0,
  VehicleStock: 1,
};

const ImagePickerModal = observer(() => {
  const { filters, setters } = useFilters();
  const { imagePickerStore } = useTemplateEditorStore();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredCars, setFilteredCars] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState({
    count: 0,
    currentPage: 0,
    pageSize: 0,
  });

  const [responsePageSize, setResponsePageSize] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCurrentPage, setSelectedCurrentPage] = useState(1);
  const totalPages = Math.ceil(responsePageSize / 3);

  const getVisiblePages = useCallback(() => {
    const start = (currentPage - 1) * 3 + 1;
    const end = Math.min(start + 2, responsePageSize);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, responsePageSize]);

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const [searchText, setSearchText] = useState("");
  const [currentFilter, setCurrentFilter] = useState(CurrentFilterEnum.MAKE);
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [entries, setEntries] = useState("10");
  const [currentTab, setCurrentTab] = useState(MediaLibraryTab.Upload);
  const debounceTimeoutRef = useRef(null);

  const handleTextSearch = (e) => {
    const text = e.target.value;
    setSearchText(text);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(text);
    }, 1000);
  };

  const visiblePages = getVisiblePages();

  const handleRequestToGetFiltersResponse = async (props) => {
    const { parameters, filter_name, search, filters } = props;
    const [response, filtersResponse] = await Promise.all([
      imagePickerStore.getVehiclesStockPicturesRequest(parameters),
      imagePickerStore.getVehiclesStockPicturesFilters({
        filter_name,
        search,
        filters,
      }),
    ]);
    return {
      response,
      filtersResponse,
    };
  };

  const getStaticFilterByName = async (filter_name) => {
    const response = await imagePickerStore.getVehiclesStockPicturesFilters({
      filter_name,
      search: "",
      filters: {},
    });

    return response?.[filter_name.toLowerCase()] ?? [];
  };

  useEffect(() => {
    const getStaticFilter = async () => {
      const [types, angles, backgrounds, sizes] = await Promise.all([
        getStaticFilterByName(CurrentFilterEnum.TYPE),
        getStaticFilterByName(CurrentFilterEnum.ANGLE),
        getStaticFilterByName(CurrentFilterEnum.BACKGROUND),
        getStaticFilterByName(CurrentFilterEnum.SIZE),
      ]);

      setters.setFiltersData.setTypes(types);
      setters.setFiltersData.setAngles(angles);
      setters.setFiltersData.setBackgrounds(backgrounds);
      setters.setFiltersData.setSizes(sizes);
    };

    getStaticFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getImageStock = async () => {
    if (!filters || !setters) return;

    setIsLoading(true);

    const filtersPayload = {
      make: filters.selectedMake,
      model: filters.selectedModel,
      year: filters.selectedYear,
      type: filters.selectedType,
      angle: filters.selectedAngle,
      background: filters.selectedBackground,
      size: filters.selectedSize,
    };

    const filtersWithValues = Object.fromEntries(
      Object.entries(filtersPayload).filter(
        ([key, value]) => value && value.length > 0
      )
    );

    const parameters = {
      page_size: entries,
      page: selectedCurrentPage,
      order_by: {
        id: "1",
        desc: true,
      },
      search: debouncedSearchText,
      filters: filtersWithValues,
    };

    const { response, filtersResponse } =
      await handleRequestToGetFiltersResponse({
        parameters,
        filter_name: currentFilter,
        search: debouncedSearchText,
        filters: filtersWithValues,
      });

    const filterMap = {
      [CurrentFilterEnum.MAKE]: setters.setFiltersData.setMake,
      [CurrentFilterEnum.MODEL]: setters.setFiltersData.setModels,
      [CurrentFilterEnum.YEAR]: setters.setFiltersData.setYears,
    };

    if (filterMap[currentFilter]) {
      filterMap[currentFilter](
        filtersResponse?.[currentFilter.toLowerCase()] || []
      );
    }

    setFilteredCars(response?.vehicles || []);
    setTotalRowCount(response?.totalRowCount || { count: 0 });
    setResponsePageSize(response?.pageSize || 1);

    setIsLoading(false);
  };

  useEffect(() => {
    getImageStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter]);

  useEffect(() => {
    if (!filters || !setters) return;
    setCurrentPage(1);
    setSelectedCurrentPage(1);
    getImageStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.selectedMake,
    filters.selectedModel,
    filters.selectedYear,
    filters.selectedType,
    filters.selectedAngle,
    filters.selectedBackground,
    filters.selectedSize,
    entries,
    debouncedSearchText,
  ]);

  const onHandleCloseModal = () => {
    imagePickerStore.setIsOpenCustomLibraryModal(false);
    handleClearAllFilters();
  };

  const handleFile = (file) => {
    imagePickerStore.onPickImage({ url: file });
    imagePickerStore.setIsOpenCustomLibraryModal(false);
  };

  const selectPage = (page) => {
    setSelectedCurrentPage(page);
    getImageStock();
  };

  const handleClearAllFilters = () => {
    setters.setSize([]);
    setters.setBackground([]);
    setters.setAngle([]);
    setters.setType([]);
    setters.setMake([]);
    setters.setModel([]);
    setters.setYear([]);
    setSearchText("");
    setDebouncedSearchText("");

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    getImageStock();
  };

  return (
    <Modal
      open={imagePickerStore.isOpenCustomLibraryModal}
      onClose={onHandleCloseModal}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <ModalContainer>
        <ModalContent>
          <ModalCustomHeader>
            <ModalTitle>
              <p>My media library</p>
              <Tabs2
                defaultTab={currentTab}
                tabs={tabsCustomLibrary}
                onToggleTab={(tab) => setCurrentTab(tab)}
              />
            </ModalTitle>
            <CloseIcon fontSize="small" onClick={onHandleCloseModal} />
          </ModalCustomHeader>

          <Tabs2.Outlet show={currentTab === MediaLibraryTab.Upload}>
            <UploadTab handleFile={handleFile} />
          </Tabs2.Outlet>
          <Tabs2.Outlet show={currentTab === MediaLibraryTab.VehicleStock}>
            <FiltersTab
              filters={filters}
              setters={setters}
              isLoading={isLoading}
              handleTextSearchDebounced={handleTextSearch}
              handleClearAllFilters={handleClearAllFilters}
              searchText={searchText}
              setCurrentFilter={setCurrentFilter}
            />
            <ImagesGrid
              images={filteredCars}
              isLoading={isLoading}
              onClickImage={handleFile}
            />
            <Paginations
              entries={entries}
              setEntries={setEntries}
              totalRowCount={totalRowCount}
              visiblePages={visiblePages}
              currentPage={currentPage}
              totalPages={totalPages}
              handlePrev={handlePrev}
              selectPage={selectPage}
              handleNext={handleNext}
              selectedCurrentPage={selectedCurrentPage}
              isLoading={isLoading}
            />
          </Tabs2.Outlet>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
});

export default ImagePickerModal;
